import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import { Plan } from "../../redux/slices/plan";
import {
  ManagedSubscription,
  SubscriptionStatus,
} from "../../redux/slices/subscription";

const formatDate = (val: string | Date | null | undefined) => {
  if (!val) return "—";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fi-FI");
};

const formatEur = (cents: number) =>
  (cents / 100).toLocaleString("fi-FI", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusLabel = (status: string) => {
  switch (status) {
    case "trialing":
      return "Kokeilu";
    case "active":
      return "Aktiivinen";
    case "past_due":
      return "Erääntynyt";
    case "canceled":
      return "Peruttu";
    case "suspended":
      return "Jäädytetty";
    case "expired":
      return "Vanhentunut";
    default:
      return status;
  }
};

/**
 * The billing period the activation will write. An already-active subscription
 * keeps its period so a mid-period plan swap doesn't push the renewal date;
 * everything else starts a fresh year from today. Both dates must end up set —
 * a NULL currentPeriodEnd is invisible to the renewal scan and to the expiry
 * sweep, i.e. a subscription that is never invoiced and never expires.
 */
export const resolveBillingPeriod = (
  sub: ManagedSubscription
): { start: Date; end: Date; kept: boolean } => {
  const keep =
    sub.status === SubscriptionStatus.ACTIVE &&
    !!sub.currentPeriodStart &&
    !!sub.currentPeriodEnd;

  if (keep) {
    return {
      start: new Date(sub.currentPeriodStart as string),
      end: new Date(sub.currentPeriodEnd as string),
      kept: true,
    };
  }

  const start = new Date();
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  return { start, end, kept: false };
};

interface SubscriptionPlanDialogProps {
  subscription: ManagedSubscription | null;
  plans: Plan[];
  working: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (planId: number, createInvoice: boolean) => void;
}

const SubscriptionPlanDialog = ({
  subscription,
  plans,
  working,
  error,
  onClose,
  onConfirm,
}: SubscriptionPlanDialogProps) => {
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [createInvoice, setCreateInvoice] = useState(true);

  useEffect(() => {
    if (!subscription) return;
    setSelectedPlanId(subscription.plan?.id?.toString() ?? "");
    setCreateInvoice(true);
  }, [subscription]);

  if (!subscription) return null;

  const selectedPlan = plans.find((p) => p.id.toString() === selectedPlanId);
  const entitlements = selectedPlan?.metadata?.entitlements ?? {};
  const period = resolveBillingPeriod(subscription);
  // The backend multiplies the posted amountCents by 12, so this is what the
  // invoice will actually say.
  const invoiceTotalCents = selectedPlan ? selectedPlan.priceCents * 12 : 0;
  const alreadyActive = subscription.status === SubscriptionStatus.ACTIVE;

  return (
    <Dialog open onClose={() => !working && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {alreadyActive ? "Vaihda paketti" : "Aktivoi tilaus"}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" fontWeight={500}>
          {subscription.association?.name ?? "—"}
        </Typography>
        <Box display="flex" alignItems="center" gap={1} mt={1} mb={3}>
          <Chip label={statusLabel(subscription.status)} size="small" />
          <Typography variant="body2" color="text.secondary">
            {subscription.plan?.name ?? "Ei pakettia"} · kausi{" "}
            {formatDate(subscription.currentPeriodStart)} –{" "}
            {formatDate(subscription.currentPeriodEnd)}
          </Typography>
        </Box>

        <FormControl fullWidth>
          <InputLabel id="superadmin-plan-select-label">Paketti</InputLabel>
          <Select
            labelId="superadmin-plan-select-label"
            id="superadmin-plan-select"
            value={selectedPlanId}
            label="Paketti"
            onChange={(e) => setSelectedPlanId(e.target.value as string)}
          >
            {plans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id.toString()}>
                {plan.name} — {formatEur(plan.priceCents)} €/kk
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedPlan && (
          <>
            <Typography variant="body2" mt={2}>
              Laskutetaan 12 kk kerralla:{" "}
              <strong>{formatEur(invoiceTotalCents)} €</strong>
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              mt={1}
            >
              <ul style={{ marginTop: 4 }}>
                <li>
                  <strong>Tallennustila:</strong> {entitlements.storage_mb} MB
                </li>
                <li>
                  <strong>Jäsenraja:</strong> {entitlements.members_limit}
                </li>
                <li>
                  <strong>Laskujen lähetys:</strong>{" "}
                  {entitlements.send_invoices ? "Kyllä" : "Ei"}
                </li>
                <li>
                  <strong>Kutsujen lähetys:</strong>{" "}
                  {entitlements.send_meeting_invites ? "Kyllä" : "Ei"}
                </li>
              </ul>
            </Typography>
          </>
        )}

        <Typography variant="body2" mt={2}>
          Tilaus merkitään aktiiviseksi, kausi{" "}
          <strong>
            {formatDate(period.start)} – {formatDate(period.end)}
          </strong>
          {period.kept ? " (nykyinen kausi säilyy)" : ""}.
        </Typography>

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              checked={createInvoice}
              onChange={(e) => setCreateInvoice(e.target.checked)}
            />
          }
          label="Luo tilauslasku (PDF + sähköposti ylläpitäjälle)"
        />

        {error && (
          <Typography variant="body2" color="error" mt={2}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={working}>
          Peruuta
        </Button>
        <Button
          variant="contained"
          disabled={working || !selectedPlan}
          onClick={() =>
            selectedPlan && onConfirm(selectedPlan.id, createInvoice)
          }
        >
          {working
            ? "Tallennetaan…"
            : alreadyActive
            ? "Vaihda paketti"
            : "Aktivoi"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionPlanDialog;
